import { api } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity } from "@app/hal";
import { selectOrganizationSelectedId } from "@app/organizations";
import { type WebState, schema } from "@app/schema";
import type {
  DeployLlmGatewayConfiguration,
  DeployLlmGatewayConfigurationResponse,
} from "@app/types";

type LlmGatewayConfigurationsResponse = {
  _embedded: {
    llm_gateway_configurations: DeployLlmGatewayConfigurationResponse[];
  };
  self_service_enrollment_enabled?: boolean;
};

export const deserializeLlmGatewayConfiguration = (
  payload: DeployLlmGatewayConfigurationResponse,
): DeployLlmGatewayConfiguration => ({
  id: `${payload.id}`,
  organizationId: payload.organization_id,
  teamId: payload.team_id,
  spendLimit: Number.parseFloat(payload.spend_limit),
  trial: payload.trial,
  models: payload.models,
  // null means usage could not be determined, which is distinct from zero
  currentUsage: payload.current_usage
    ? {
        spend: payload.current_usage.spend,
        budgetResetAt: payload.current_usage.budget_reset_at,
      }
    : null,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

export const selectLlmGatewayConfigurationsAsList =
  schema.llmGatewayConfigurations.selectTableAsList;

export const selectLlmGatewaySelfServiceEnrollmentEnabled =
  schema.llmGatewaySelfServiceEnrollment.select;

export const selectLlmGatewayConfiguration = createSelector(
  selectLlmGatewayConfigurationsAsList,
  (s: WebState) => selectOrganizationSelectedId(s),
  (configs, orgId) => configs.find((c) => c.organizationId === orgId) ?? null,
);

export const fetchLlmGatewayConfiguration = api.get<
  { orgId: string },
  LlmGatewayConfigurationsResponse
>(
  "/llm_gateway_configurations?organization_id=:orgId&usage=true",
  function* (ctx, next) {
    const { orgId } = ctx.payload;
    if (!orgId) return;

    yield* next();
    if (!ctx.json.ok) return;

    yield* schema.update([
      schema.llmGatewayConfigurations.reset(),
      schema.llmGatewaySelfServiceEnrollment.set(
        ctx.json.value.self_service_enrollment_enabled !== false,
      ),
    ]);
  },
);

export const createLlmGatewayConfiguration = api.post<{ orgId: string }>(
  "/llm_gateway_configurations?organization_id=:orgId",
  function* (ctx, next) {
    yield* next();
    if (ctx.response?.status === 404) {
      yield* schema.update(schema.llmGatewaySelfServiceEnrollment.set(false));
    }
  },
);

/**
 * Registers interest in a trial. Access is granted by a human afterwards, so a
 * success here means the request was recorded, not that the gateway is usable.
 */
export const requestLlmGatewayAccess = api.post<{ orgId: string }>(
  "/llm_gateway_configurations/access_request?organization_id=:orgId",
);

export const updateLlmGatewayConfiguration = api.patch<{
  orgId: string;
  id: string;
  spendLimit?: number;
  models?: string[];
}>(
  "/llm_gateway_configurations/:id?organization_id=:orgId",
  function* (ctx, next) {
    const { spendLimit, models } = ctx.payload;

    const body = {
      ...(spendLimit === undefined ? {} : { spend_limit: spendLimit }),
      ...(models === undefined ? {} : { models }),
    };

    ctx.request = ctx.req({ body: JSON.stringify(body) });
    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    ctx.loader = { message: "Successfully updated LLM Gateway settings!" };
  },
);

export const llmGatewayConfigurationEntities = {
  llm_gateway_configuration: defaultEntity({
    id: "llm_gateway_configuration",
    deserialize: deserializeLlmGatewayConfiguration,
    save: schema.llmGatewayConfigurations.add,
  }),
};
